// @ts-strict-ignore
import {Component, Injector} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {
	AudioDeviceInputChannelType,
	AudioDeviceMode,
	AudioDeviceOutputChannelType,
	AudioInputChannel,
	AudioOutputChannel,
	TrainingRoom,
} from "@models";
import Enumerable from "linq";
import {
	SimAudioChannelService,
	SimConfigurationService,
	NotificationService,
	SimSystemExtendedService,
	SimTrainingRoomService,
} from "@services";
import {Translations} from "@translations";

export interface DialogData_AudioChannelSelectionDialogInput extends MatDialogDataBaseInput {
	trainingRoom: TrainingRoom;
}

export interface DialogData_AudioChannelSelectionDialogOutput extends MatDialogDataBaseOutput {
	updatedTrainingRoom: TrainingRoom;
}

@Component({
	selector: "sim-audio-selection-dialog",
	templateUrl: "./audio-selection-dialog.component.html",
	styleUrls: ["./audio-selection-dialog.component.scss"],
	standalone: false,
})
export class AudioSelectionDialogComponent extends BasicMaterialDialog<
	AudioSelectionDialogComponent,
	DialogData_AudioChannelSelectionDialogInput,
	DialogData_AudioChannelSelectionDialogOutput
> {
	T = Translations;
	AudioDeviceMode = AudioDeviceMode;

	settings = SimConfigurationService.settings;

	public vopChannel: number = null;
	public vogChannel: number = null;
	public vttChannel: number = null;
	public inputChannelId: string = null;

	public availableVogAudioOutputChannels: AudioOutputChannel[] = [];
	public availableVopAudioOutputChannels: AudioOutputChannel[] = [];
	public availableVttAudioOutputChannels: AudioOutputChannel[] = [];
	public availableInputChannels: AudioInputChannel[] = [];

	private audioInputChannels: AudioInputChannel[] = null;
	private audioOutputChannels: AudioOutputChannel[] = null;

	private audioInputChannelDictionary: any = {};
	private audioOutputChannelDictionary: any = {};

	public loadingAudioDeviceChannelsTask: Promise<any> = null;

	public filteredInputChannels: AudioInputChannel[] = null;
	public filteredVogOutputChannels: AudioOutputChannel[] = null;
	public filteredVopOutputChannels: AudioOutputChannel[] = null;
	public filteredVttOutputChannels: AudioOutputChannel[] = null;

	constructor(
		_injector: Injector,
		private _simAudioChannelService: SimAudioChannelService,
	) {
		super(_injector);

		if (this.data.trainingRoom) {
			// ------------------------------------------- //
			// Initialize with empty array                 //
			// ------------------------------------------- //
			this.data.trainingRoom.audioChannelInputs = this.data.trainingRoom.audioChannelInputs || [];
			this.data.trainingRoom.audioInputChannelIds = this.data.trainingRoom.audioInputChannelIds || [];

			this.data.trainingRoom.vogAudioOutputChannels = this.data.trainingRoom.vogAudioOutputChannels || [];
			this.data.trainingRoom.vopAudioOutputChannels = this.data.trainingRoom.vopAudioOutputChannels || [];
			this.data.trainingRoom.vttAudioOutputChannels = this.data.trainingRoom.vttAudioOutputChannels || [];

			// ------------------------------------------- //
			// Remove duplicate audio channels             //
			// ------------------------------------------- //
			this.data.trainingRoom.audioChannelInputs = Enumerable.from(this.data.trainingRoom.audioChannelInputs).distinct().toArray();

			this.data.trainingRoom.vogAudioOutputChannels = Enumerable.from(this.data.trainingRoom.vogAudioOutputChannels)
				.distinct()
				.toArray();
			this.data.trainingRoom.vopAudioOutputChannels = Enumerable.from(this.data.trainingRoom.vopAudioOutputChannels)
				.distinct()
				.toArray();
			this.data.trainingRoom.vttAudioOutputChannels = Enumerable.from(this.data.trainingRoom.vttAudioOutputChannels)
				.distinct()
				.toArray();
		}

		this.loadAudioDeviceChannels();
	}

	private loadAudioDeviceChannels() {
		this.audioInputChannelDictionary = {};
		this.audioOutputChannelDictionary = {};

		const task1 = this._simAudioChannelService.getAllAudioInputChannels().then((getAudioInputChannelsResult) => {
			this.audioInputChannels = getAudioInputChannelsResult.sort((a, b) => a.displayAdminText.localeCompare(b.displayAdminText));

			if (this.audioInputChannels && this.audioInputChannels.length > 0) {
				this.availableInputChannels = [];

				for (const audioInputChannel of this.audioInputChannels) {
					// create dictionary for all channels
					this.audioInputChannelDictionary[audioInputChannel.id] = audioInputChannel;

					if (audioInputChannel.type === AudioDeviceInputChannelType.Default) {
						this.availableInputChannels.push(audioInputChannel);
					}
				}

				this.filteredInputChannels = null;
			}
		});

		const task2 = this._simAudioChannelService.getAudioOutputChannels().then((getAudioOutputChannelsResult) => {
			this.audioOutputChannels = getAudioOutputChannelsResult.sort((a, b) => a.displayAdminText.localeCompare(b.displayAdminText));

			if (this.audioOutputChannels && this.audioOutputChannels.length > 0) {
				this.availableVogAudioOutputChannels = [];
				this.availableVopAudioOutputChannels = [];
				this.availableVttAudioOutputChannels = [];

				for (const audioOutputChannel of this.audioOutputChannels) {
					// create dictionary for all channels
					this.audioOutputChannelDictionary[audioOutputChannel.channel] = audioOutputChannel;

					if (
						audioOutputChannel.type === AudioDeviceOutputChannelType.VogOutputChannel ||
						audioOutputChannel.type === AudioDeviceOutputChannelType.TrainingRoomOutputChannel ||
						audioOutputChannel.type == AudioDeviceOutputChannelType.Exstreamer
					) {
						this.availableVogAudioOutputChannels.push(audioOutputChannel);
					}

					if (
						audioOutputChannel.type === AudioDeviceOutputChannelType.VopOutputChannel ||
						audioOutputChannel.type === AudioDeviceOutputChannelType.TrainingRoomOutputChannel ||
						audioOutputChannel.type == AudioDeviceOutputChannelType.Exstreamer
					) {
						this.availableVopAudioOutputChannels.push(audioOutputChannel);
					}

					if (
						audioOutputChannel.type === AudioDeviceOutputChannelType.VttOutputChannel ||
						audioOutputChannel.type === AudioDeviceOutputChannelType.TrainingRoomOutputChannel
					) {
						this.availableVttAudioOutputChannels.push(audioOutputChannel);
					}
				}
			}

			this.filteredVogOutputChannels = null;
			this.filteredVopOutputChannels = null;
			this.filteredVttOutputChannels = null;
		});

		this.loadingAudioDeviceChannelsTask = Promise.all([task1, task2]);
	}

	public getInputChannelName(channelId: string) {
		if (this.audioInputChannelDictionary[channelId]) {
			return this.audioInputChannelDictionary[channelId].displayAdminText;
		} else {
			return `Input Channel not defined: ${channelId}`;
		}
	}

	public getOutputChannelName(channel: number) {
		if (this.audioOutputChannelDictionary[channel]) {
			return this.audioOutputChannelDictionary[channel].displayAdminText;
		} else {
			return `Output Channel not defined: ${channel}`;
		}
	}

	public addChannelById(channelId: string, channelIds: string[]) {
		if (!!channelId && channelIds.indexOf(channelId) < 0) {
			channelIds.push(channelId);
		}
		this.resetFilters();
	}

	public removeChannelById(channelId: string) {
		this.data.trainingRoom?.audioInputChannelIds.splice(this.data.trainingRoom?.audioInputChannelIds.indexOf(channelId), 1);
		this.resetFilters();
	}

	public addChannel(channel: number, channels: number[]) {
		if (channels.indexOf(channel) < 0) {
			channels.push(channel);
		}
		this.resetFilters();
	}

	public removeChannel(channel: number, channels: number[]) {
		channels.splice(channels.indexOf(channel), 1);
		this.resetFilters();
	}

	ok() {
		this.updateIdsFromChannels();
		this.close({action: "ok", updatedTrainingRoom: this.data.trainingRoom});
	}

	public filterInputChannels() {
		if (!!this.availableInputChannels && !!this.data?.trainingRoom?.audioInputChannelIds) {
			this.filteredInputChannels = this.availableInputChannels.filter(
				(audioChannel) => !this.data.trainingRoom.audioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.inputChannelId = null;
		return this.filteredInputChannels;
	}

	public filterVogOutputChannels() {
		if (!!this.availableVogAudioOutputChannels && !!this.data?.trainingRoom?.vogAudioOutputChannels) {
			this.filteredVogOutputChannels = this.availableVogAudioOutputChannels.filter(
				(audioChannel) => !this.data.trainingRoom.vogAudioOutputChannels.includes(audioChannel.channel),
			);
		}
		this.vogChannel = null;
		return this.filteredVogOutputChannels;
	}

	public filterVopOutputChannels() {
		if (!!this.availableVopAudioOutputChannels && !!this.data?.trainingRoom?.vopAudioOutputChannels) {
			this.filteredVopOutputChannels = this.availableVopAudioOutputChannels.filter(
				(audioChannel) => !this.data.trainingRoom.vopAudioOutputChannels.includes(audioChannel.channel),
			);
		}
		this.vopChannel = null;
		return this.filteredVopOutputChannels;
	}

	public filterVttOutputChannels() {
		if (!!this.availableVttAudioOutputChannels && !!this.data?.trainingRoom?.vttAudioOutputChannels) {
			this.filteredVttOutputChannels = this.availableVttAudioOutputChannels.filter(
				(audioChannel) => !this.data.trainingRoom.vttAudioOutputChannels.includes(audioChannel.channel),
			);
		}
		this.vttChannel = null;

		return this.filteredVttOutputChannels;
	}

	private resetFilters() {
		this.filteredInputChannels = null;
		this.filteredVogOutputChannels = null;
		this.filteredVopOutputChannels = null;
		this.filteredVttOutputChannels = null;
	}

	private updateIdsFromChannels() {
		const trainingRoom = this.data.trainingRoom as TrainingRoom;
		trainingRoom.vogAudioOutputChannelIds = [];
		trainingRoom.vopAudioOutputChannelIds = [];
		trainingRoom.vttAudioOutputChannelIds = [];

		if (trainingRoom.vogAudioOutputChannels) {
			for (const channelNumber of trainingRoom.vogAudioOutputChannels) {
				const channel = this.audioOutputChannelDictionary[channelNumber] as AudioOutputChannel;
				if (channel) {
					trainingRoom.vogAudioOutputChannelIds.push(channel.id);
				}
			}
		}
		if (trainingRoom.vopAudioOutputChannels) {
			for (const channelNumber of trainingRoom.vopAudioOutputChannels) {
				const channel = this.audioOutputChannelDictionary[channelNumber] as AudioOutputChannel;
				if (channel) {
					trainingRoom.vopAudioOutputChannelIds.push(channel.id);
				}
			}
		}
		if (trainingRoom.vttAudioOutputChannels) {
			for (const channelNumber of trainingRoom.vttAudioOutputChannels) {
				const channel = this.audioOutputChannelDictionary[channelNumber] as AudioOutputChannel;
				if (channel) {
					trainingRoom.vttAudioOutputChannelIds.push(channel.id);
				}
			}
		}
	}
}
